import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import SendIcon from "@mui/icons-material/Send";
import SEO from "../SEO";
import { useTranslation } from "react-i18next";

const MobileServiceGuideDetailsItem = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { t } = useTranslation();

  if (!location.state) {
    nav("/services-guide");
    return null;
  }
  const currencyName = location.state?.currency?.[0].name;
  const {
    name,
    applicationFee,
    compilationTime,
    isPdfExist,
    requiredAttachments = [],
    pdfPath,
    isForCitizen,
    workflowSteps = [],
  } = location.state;

  const infoCards = [
    {
      title: t("serviceGuideDetails_fee"),
      value: `${applicationFee ?? "-"} ${currencyName}`.trim(),
      icon: <ReceiptLongOutlinedIcon sx={{ color: "#2E7D32" }} />,
      tint: "#FFFFFF",
      border: "rgba(15,23,42,0.08)",
      valueColor: "#2E7D32",
    },
    {
      title: t("serviceGuideDetails_duration"),
      value: compilationTime ? t("serviceGuideDetails_durationDays", { count: compilationTime, days: compilationTime }) : t("serviceGuideDetails_notSpecified"),
      icon: <ScheduleRoundedIcon sx={{ color: "#0F6CBD" }} />,
      tint: "#FFFFFF",
      border: "rgba(15,23,42,0.08)",
      valueColor: "#0F6CBD",
    },
  ];

  const openPdf = () => {
    window.open(pdfPath, "_blank");
  };

  const handleRequestService = () => {
    nav("/service-request", { state: location.state });
  };

  return (
    <>
      <SEO title={name} />
      <Box sx={{ py: 1 }}>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Stack gap={2}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Stack gap={1.25}>
                <Typography
                  sx={{
                    width: "fit-content",
                    px: 1.4,
                    py: 0.65,
                    borderRadius: 99,
                    backgroundColor: "action.hover",
                    color: "text.secondary",
                    fontWeight: 800,
                    fontSize: "0.82rem",
                  }}
                >
                  {t("serviceGuideDetails_badge")}
                </Typography>
                <Typography sx={{ fontSize: "1.45rem", fontWeight: 800, color: "#2B2320", lineHeight: 1.45 }}>
                  {name}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "0.95rem", lineHeight: 1.8 }}>
                  {t("serviceGuideDetails_intro")}
                </Typography>

                <Alert
                  severity={isForCitizen ? "success" : "info"}
                  sx={{
                    mt: 0.5,
                    border: "1px solid",
                    borderColor: isForCitizen ? "success.light" : "divider",
                    alignItems: "center",
                    "& .MuiAlert-message": { fontWeight: 700, fontSize: "0.92rem" },
                  }}
                >
                  {isForCitizen
                    ? t("serviceGuideDetails_onlineAlert")
                    : t("serviceGuideDetails_municipalityAlert")}
                </Alert>
              </Stack>
            </Paper>

            <Stack gap={1.25}>
              {isForCitizen && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  onClick={handleRequestService}
                  sx={{
                    py: 1.45,
                    borderRadius: 99,
                    fontWeight: 800,
                    boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
                  }}
                >
                  {t("nav_submitRequest", "تقديم الطلب")}
                </Button>
              )}
              <Button
                variant="outlined"
                fullWidth
                onClick={openPdf}
                disabled={!isPdfExist}
                startIcon={<DescriptionOutlinedIcon />}
                sx={{
                  py: 1.35,
                  borderRadius: 99,
                  backgroundColor: "background.paper",
                  fontWeight: 800,
                }}
              >
                {t("serviceGuideDetails_openGuide")}
              </Button>
            </Stack>

            <Stack gap={1.25}>
              {infoCards.map((card) => (
                <Paper
                  key={card.title}
                  elevation={0}
                  sx={{
                    p: 1.75,
                    border: `1px solid ${card.border}`,
                    background: card.tint,
                    boxShadow: "none",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "16px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.paper",
                        border: `1px solid ${card.border}`,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Stack sx={{ flex: 1, textAlign: "center" }} gap={0.45}>
                      <Typography sx={{ color: "#7A6A5E", fontWeight: 700, fontSize: "0.86rem" }}>
                        {card.title}
                      </Typography>
                      <Typography sx={{ color: card.valueColor, fontWeight: 800, fontSize: "1rem" }}>
                        {card.value}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
              <Typography sx={{ textAlign: "center", fontWeight: 800, color: "#2B2320", fontSize: "1.06rem", mb: 1.5 }}>
                {t("serviceGuideDetails_requiredAttachments")}
              </Typography>

              {requiredAttachments.length > 0 ? (
                <Stack gap={1}>
                  {requiredAttachments.map((attachment, index) => (
                    <Paper
                      key={`${attachment.attachmentType}-${index}`}
                      elevation={0}
                      sx={{
                        p: 1.6,
                        border: "1px solid #E9DDD1",
                        backgroundColor: index % 2 === 0 ? "#FFFDFC" : "#FBF7F2",
                      }}
                    >
                      <Stack gap={1.1}>
                        <Typography sx={{ textAlign: "center", fontWeight: 800, color: "#2B2320" }}>
                          {attachment.attachmentType}
                        </Typography>
                        <Stack direction="row" justifyContent="center" gap={1.2}>
                          <Paper elevation={0} sx={{ px: 1.25, py: 0.8, borderRadius: 99, backgroundColor: attachment.isMandatory ? "rgba(46,125,50,0.10)" : "#F4ECE4" }}>
                            <Stack direction="row" gap={0.6} alignItems="center">
                              {attachment.isMandatory && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#2E7D32" }} />}
                              <Typography sx={{ fontWeight: 700, color: attachment.isMandatory ? "#2E7D32" : "#8B7E73" }}>
                                {t("serviceGuideDetails_mandatory")}
                              </Typography>
                            </Stack>
                          </Paper>
                          <Paper elevation={0} sx={{ px: 1.25, py: 0.8, borderRadius: 99, backgroundColor: !attachment.isMandatory ? "rgba(15,108,189,0.10)" : "#F4ECE4" }}>
                            <Stack direction="row" gap={0.6} alignItems="center">
                              {!attachment.isMandatory && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#0F6CBD" }} />}
                              <Typography sx={{ fontWeight: 700, color: !attachment.isMandatory ? "#0F6CBD" : "#8B7E73" }}>
                                {t("serviceGuideDetails_optional")}
                              </Typography>
                            </Stack>
                          </Paper>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Paper elevation={0} sx={{ p: 2.25, textAlign: "center", backgroundColor: "#FBF7F2", border: "1px dashed #D8C7B9" }}>
                  <Typography sx={{ fontWeight: 700, color: "#75685F" }}>
                    {t("serviceGuideDetails_noAttachments")}
                  </Typography>
                </Paper>
              )}
            </Paper>

            {workflowSteps.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}>
                <Typography sx={{ textAlign: "center", fontWeight: 800, color: "#2B2320", fontSize: "1.06rem", mb: 1.5 }}>
                  {t("serviceGuideDetails_workflow")}
                </Typography>
                <Stack gap={1.2}>
                  {workflowSteps.map((step, index) => (
                    <Box key={`${step}-${index}`}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "38px 1fr", gap: 1.2, alignItems: "stretch" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: "primary.main",
                              color: "#fff",
                              fontWeight: 800,
                              fontSize: "1rem",
                            }}
                          >
                            {index + 1}
                          </Box>
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.4,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        >
                          <Typography sx={{ fontWeight: 800, textAlign: "center", color: "#2D2622", lineHeight: 1.7 }}>
                            {step}
                          </Typography>
                        </Paper>
                      </Box>

                      {index < workflowSteps.length - 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 0.6,
                            color: "text.disabled",
                          }}
                        >
                          <SouthRoundedIcon fontSize="small" />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>
      </Box>
    </>
  );
};
export default MobileServiceGuideDetailsItem;
